import { useEffect, useState } from 'react';
import { HOSTNAME_TEMPLATE_DEFAULT, defaultHostname, parseDomain, type RehostChangeDTO, type VpsDTO, type WithRerouteJobs } from '@devrobot/shared';
import { ApiError, api, seg } from '../../api/client';
import { qk, useApiMutation, useSettings } from '../../api/queries';
import { CopyButton } from '../../components/CopyButton';
import { ErrorAlert } from '../../components/ErrorAlert';
import { RehostConfirmDialog } from '../../components/RehostConfirmDialog';
import { useToast } from '../../components/Toaster';
import { Haptic } from '../../kit/haptics';
import { haptic } from '../../kit/native';

const IPV4_RE = /^(\d{1,3}\.){3}\d{1,3}$/;
const IP_RE = /^(\d{1,3}\.){3}\d{1,3}$|^[0-9a-f]*:[0-9a-f:.]*$/i;

type Body = { domain?: string | null; publicIp?: string | null; confirmMove?: boolean };

/** Domain for generated application hostnames and the public IP used for DNS instructions. */
export function VpsAddressEditor({ vps, editable }: { vps: VpsDTO; editable: boolean }) {
  const toast = useToast();
  const template = useSettings().data?.hostnameTemplate ?? HOSTNAME_TEMPLATE_DEFAULT;
  const [domain, setDomain] = useState(vps.domain ?? '');
  const [publicIp, setPublicIp] = useState(vps.publicIp ?? '');
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState<{ body: Body; changes: RehostChangeDTO[] } | null>(null);
  useEffect(() => {
    if (dirty) return;
    setDomain(vps.domain ?? '');
    setPublicIp(vps.publicIp ?? '');
  }, [vps.domain, vps.publicIp, dirty]);
  const save = useApiMutation((body: Body) => api.patch<WithRerouteJobs<VpsDTO>>(`/api/v1/vps/${seg(vps.id)}`, body), { invalidate: [['vps'], qk.vps(vps.id), ['jobs'], ['overview']] });

  const parsed = domain.trim() ? parseDomain(domain) : null;
  const nextDomain = parsed?.ok ? parsed.domain : null;
  const nextIp = publicIp.trim() || null;
  const ipValid = !nextIp || IP_RE.test(nextIp);
  const invalid = Boolean(parsed && !parsed.ok) || !ipValid;
  const body: Body = {
    ...(nextDomain !== vps.domain ? { domain: nextDomain } : {}),
    ...(nextIp !== vps.publicIp ? { publicIp: nextIp } : {}),
  };
  const changed = Object.keys(body).length > 0;
  const dnsValue = vps.publicIp ?? (IPV4_RE.test(vps.host) ? vps.host : '<public IP of the VPS>');

  const submit = (b: Body) =>
    save.mutate(b, {
      onSuccess: (r) => {
        setDirty(false);
        setPending(null);
        const moving = r.rerouteJobs?.length ?? 0;
        if (moving) toast.success('Public address saved', { description: `${moving} application${moving === 1 ? ' is' : 's are'} moving to the new hostname; follow the jobs under “In progress”.` });
        else toast.success('Public address saved');
      },
      onError: (err) => {
        // The server lists the applications that would move and waits for an explicit confirmation.
        if (err instanceof ApiError && err.confirmation?.field === 'confirmMove') {
          save.reset();
          setPending({ body: b, changes: err.rehostChanges });
          return;
        }
        haptic('error');
      },
    });

  return (
    <>
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (save.isPending) return;
          if (invalid) {
            haptic('error');
            return;
          }
          if (changed) submit(body);
        }}
      >
        <fieldset disabled={!editable || save.isPending} className="space-y-1">
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Domain</legend>
            <input
              className={`input w-full font-mono ${parsed && !parsed.ok ? 'input-error' : ''}`}
              value={domain}
              onChange={(e) => (setDomain(e.target.value), setDirty(true))}
              placeholder="eu1.devrobot.dev"
              autoCapitalize="off"
              spellCheck={false}
              disabled={vps.proxyMode === 'none'}
              aria-describedby={`domain-hint-${vps.id}`}
            />
            <p id={`domain-hint-${vps.id}`} className="label whitespace-normal">
              {vps.proxyMode === 'none' ? (
                'Domains need the managed proxy; this server only exposes host ports.'
              ) : parsed && !parsed.ok ? (
                <span className="dr-ink-error">{parsed.error}</span>
              ) : nextDomain ? (
                <span>
                  Applications get a hostname such as <code className="break-anywhere font-mono">{defaultHostname(template, { project: 'erp', environment: 'development' }, nextDomain)}</code> unless another one is chosen when deploying.
                </span>
              ) : (
                'Without a domain, hostnames are chosen by hand when deploying.'
              )}
            </p>
          </fieldset>
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Public IP</legend>
            <input
              className={`input w-full font-mono ${ipValid ? '' : 'input-error'}`}
              value={publicIp}
              onChange={(e) => (setPublicIp(e.target.value), setDirty(true))}
              placeholder={IPV4_RE.test(vps.host) ? vps.host : '203.0.113.10'}
              autoCapitalize="off"
              spellCheck={false}
            />
            <p className="label whitespace-normal">{ipValid ? 'Used for DNS instructions and checks. Empty: the host address is used.' : <span className="dr-ink-error">Enter an IPv4 or IPv6 address.</span>}</p>
          </fieldset>
        </fieldset>
        {vps.domain ? (
          <div className="rounded-field bg-base-200/60 p-3 text-sm">
            <p className="text-xs font-medium text-base-content/55">DNS record for generated hostnames</p>
            <p className="mt-1 flex flex-wrap items-center gap-x-2 font-mono text-xs">
              <span className="break-anywhere">*.{vps.domain}</span>
              <span className="text-base-content/60">A</span>
              <span>{dnsValue}</span>
              <CopyButton value={`*.${vps.domain}`} label="Copy the record name" />
            </p>
            <p className="mt-1 text-xs text-base-content/60">Create it once at your DNS provider. Caddy then obtains a certificate for each application automatically.</p>
          </div>
        ) : null}
        <ErrorAlert error={save.error} title="The public address was not saved" />
        {editable ? (
          <div className="flex justify-end gap-2">
            <button type="button" className="btn btn-ghost btn-sm" disabled={!dirty || save.isPending} onClick={() => (setDirty(false), save.reset())}>
              Discard
            </button>
            <Haptic disabled={!changed || invalid || save.isPending}>
              <button type="submit" className="btn btn-primary btn-sm" disabled={!changed || invalid || save.isPending}>
                {save.isPending ? <span className="loading loading-spinner loading-xs" aria-hidden="true" /> : null}
                Save
              </button>
            </Haptic>
          </div>
        ) : (
          <p className="text-xs text-base-content/60">Only platform administrators can change these.</p>
        )}
      </form>
      {pending ? (
        <RehostConfirmDialog
          title={pending.body.domain ? `Move applications to ${pending.body.domain}?` : 'Move applications to new hostnames?'}
          changes={pending.changes}
          busy={save.isPending}
          error={save.error}
          onCancel={() => (setPending(null), save.reset())}
          onConfirm={() => submit({ ...pending.body, confirmMove: true })}
        />
      ) : null}
    </>
  );
}
